import { useState, useEffect } from "react";
import { Calendar, Plus, QrCode, Settings, Eye, Link } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useStoreManagementStore } from "@/store/storeManagementStore";
import { format, isToday, isTomorrow, isThisWeek } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import QRCode from "react-qr-code";
import { CreateReservationDialog } from "@/components/reservations/CreateReservationDialog";
import { ReservationCalendar } from "@/components/reservations/ReservationCalendar";
import { ReservationSettings } from "@/components/reservations/ReservationSettings";
import { useStoreReservationStore } from "@/store/storeReservationStore";

export default function ReservationsManagement() {
  const { t } = useTranslation();
  const { selectedStoreId } = useStoreManagementStore();
  const {
    reservations,
    settings,
    isLoading: loading,
    updateSettings,
    loadStoreReservationData,
    createReservation,
    selectedReservation,
    setSelectedReservation,
  } = useStoreReservationStore();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [view, setView] = useState<"calendar" | "list">("calendar");
  useEffect(() => {
    if (selectedStoreId) {
      loadStoreReservationData();
    }
  }, [selectedStoreId]);

  // Real-time subscription for reservations
  useEffect(() => {
    if (!selectedStoreId) return;
    const channel = supabase
      .channel("reservations-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "reservations",
          filter: `store_id=eq.${selectedStoreId}`,
        },
        () => {
          loadStoreReservationData();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedStoreId]);

  const getReservationBadgeVariant = (status: string) => {
    switch (status) {
      case "confirmed":
        return "default";
      case "cancelled":
        return "destructive";
      case "completed":
        return "secondary";
      default:
        return "default";
    }
  };

  const formatReservationDate = (date: string) => {
    const reservationDate = new Date(date);
    if (isToday(reservationDate)) return "Today";
    if (isTomorrow(reservationDate)) return "Tomorrow";
    if (isThisWeek(reservationDate)) return format(reservationDate, "EEEE");
    return format(reservationDate, "MMM dd, yyyy");
  };

  const publicBookingUrl = settings
    ? `${window.location.origin}/book/store-${selectedStoreId}`
    : "";

  const handlePreviewBooking = () => {
    if (settings?.public_slug) {
      window.open(`/book/store-${selectedStoreId}`, "_blank");
    }
  };

  const handleGenerateSlug = async () => {
    if (!selectedStoreId) return;

    try {
      const success = await updateSettings({
        is_public_booking_enabled: true,
      });

      if (success) {
        toast({
          title: "Success",
          description: "Public booking URL has been generated",
        });
      }
    } catch (error: any) {
      console.error("Error generating slug:", error);
      // Check for the specific constraint violation error
      const isDuplicateStoreSettings =
        error?.code === "23505" &&
        error?.message?.includes("store_reservation_settings_store_id_key");

      if (isDuplicateStoreSettings) {
        // Load the existing settings to get the current slug
        await loadStoreReservationData();
        toast({
          title: "Notice",
          description: "This store already has a public booking URL",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to generate public booking URL",
          variant: "destructive",
        });
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const handleCalendarCellClick = (date: Date) => {
    setView("list");
  };

  if (!selectedStoreId) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">{t("reservations.title")}</h2>
          <p className="text-muted-foreground">
            {t("reservations.selectStore")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight">
          {t("reservations.title")}
        </h1>
        <p className="text-muted-foreground">{t("reservations.subtitle")}</p>
      </div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex rounded-lg border p-1">
            <Button
              variant={view === "calendar" ? "default" : "ghost"}
              size="sm"
              onClick={() => setView("calendar")}
            >
              <Calendar className="h-4 w-4 mr-2" />
              {t("reservations.calendar")}
            </Button>
            <Button
              variant={view === "list" ? "default" : "ghost"}
              size="sm"
              onClick={() => setView("list")}
            >
              {t("reservations.list")}
            </Button>
          </div>

          <div className="flex flex-wrap gap-3">
            {settings?.public_slug ? (
              <Button variant="outline" onClick={handlePreviewBooking}>
                <Eye className="h-4 w-4 mr-2" />
                {t("reservations.previewBooking")}
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={handleGenerateSlug}
                disabled={loading}
              >
                <Link className="h-4 w-4 mr-2" />
                {t("reservations.generateUrl")}
              </Button>
            )}

            {settings && (
              <Button variant="outline" onClick={() => setQrDialogOpen(true)}>
                <QrCode className="h-4 w-4 mr-2" />
                {t("reservations.qrCode")}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setSettingsDialogOpen(true)}
            >
              <Settings className="h-4 w-4 mr-2" />
              {t("reservations.settings")}
            </Button>
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              {t("reservations.newReservation")}
            </Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="h-3 bg-muted rounded"></div>
                  <div className="h-3 bg-muted rounded w-4/5"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : reservations.length !== 0 ? (
        <ReservationCalendar
          reservations={reservations}
          view={view}
          setView={setView}
          onReservationSelect={(reservation) => {
            setSelectedReservation(reservation);
          }}
          onCalendarCellClick={(date) => {
            handleCalendarCellClick(date);
          }}
        />
      ) : (
        <div>
          {reservations.length === 0 && (
            <div className="col-span-full text-center py-12">
              <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                {t("reservations.noReservations")}
              </h3>
              <p className="text-muted-foreground mb-4">
                {t("reservations.noReservationsDescription")}
              </p>
              <Button onClick={() => setCreateDialogOpen(true)}>
                {t("reservations.createFirst")}
              </Button>
            </div>
          )}
        </div>
      )}

      <CreateReservationDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        storeId={selectedStoreId}
        onSuccess={loadStoreReservationData}
      />

      <ReservationSettings
        open={settingsDialogOpen}
        onOpenChange={setSettingsDialogOpen}
        storeId={selectedStoreId}
        currentSettings={settings}
        onSettingsUpdate={(newSettings) => {
          updateSettings(newSettings);
          loadStoreReservationData();
        }}
      />

      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("reservations.qrCodeTitle")}</DialogTitle>
            <DialogDescription>
              {t("reservations.qrCodeDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center space-y-4">
            {publicBookingUrl && (
              <>
                <div className="bg-white p-4 rounded-lg">
                  <QRCode value={publicBookingUrl} size={200} />
                </div>
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-2">
                    {t("reservations.bookingUrl")}:
                  </p>
                  <code className="text-xs bg-muted px-2 py-1 rounded break-all">
                    {publicBookingUrl}
                  </code>
                </div>
                <Button
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(publicBookingUrl);
                    toast({
                      title: t("common.copied"),
                      description: t("reservations.urlCopied"),
                    });
                  }}
                >
                  {t("reservations.copyUrl")}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
